import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Sparkles, ArrowRight } from 'lucide-react';
import { useVenueActionNeeded } from '../../hooks/useVenueActionNeeded';
import { setVenueSetupReturnPath } from '../../utils/venueSetupBackNav';

const FONT = "'Inria Serif', serif";

/**
 * Reusable banner that surfaces the SAME "finish setup" / "connect payout"
 * CTAs the analytics page shows, so an owner can't miss them simply by
 * living on the scan screen all day. Compact variant — tuned to sit above
 * the scan amount card without pushing the keypad off-screen.
 */
export function VenueActionNeededBanner({ fromPath = '/venue/scan', testIdPrefix = 'venue-scan' }) {
  const navigate = useNavigate();
  const { needsSetup, needsPayout } = useVenueActionNeeded();

  if (!needsSetup && !needsPayout) return null;

  // Setup first — if a venue is in setup_mode it never has an approved
  // payout gate yet, but we still priority-check so the message is right.
  const isSetup = needsSetup;
  const title = isSetup ? 'Finish setup to go live' : 'Your venue is hidden from guests';
  const subtitle = isSetup
    ? "Add photos, vibe & hours — tap to pick up where you left off."
    : "Allure approved you — connect a payout method so guests can pay.";
  const ctaLabel = isSetup ? 'Open setup' : 'Connect payout';
  const Icon = isSetup ? Sparkles : AlertTriangle;

  const bg = isSetup ? 'var(--accent-surface-soft)' : 'var(--status-warning-surface)';
  const border = isSetup ? '1px solid var(--card-border)' : '1px solid rgba(214, 137, 16, 0.25)';
  const iconBg = isSetup ? 'var(--accent-surface)' : 'rgba(214, 137, 16, 0.14)';
  const iconColor = isSetup ? 'var(--accent-purple)' : '#B56B11';
  const titleColor = isSetup ? 'var(--text)' : 'var(--status-warning-text)';
  const subtitleColor = isSetup ? 'var(--text-2)' : 'var(--status-warning-text)';
  const ctaBg = isSetup ? 'var(--accent-purple)' : 'linear-gradient(135deg, #F39A24, #D6890F)';
  const ctaShadow = isSetup ? 'var(--premium-shadow-main)' : '0 8px 16px rgba(214, 137, 16, 0.30)';

  const handleClick = () => {
    setVenueSetupReturnPath(fromPath);
    if (isSetup) {
      navigate('/venue/setup-mode', { state: { from: fromPath } });
    } else {
      navigate('/venue/settings/payouts', { state: { from: fromPath } });
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={`${testIdPrefix}-action-needed-banner`}
      data-variant={isSetup ? 'setup' : 'payout'}
      style={{
        width: 'calc(100% - 32px)',
        maxWidth: 360,
        marginTop: 12,
        padding: '12px 14px',
        borderRadius: 14,
        border,
        background: bg,
        boxShadow: isSetup ? 'var(--premium-shadow-nested)' : '0 8px 18px rgba(214, 137, 16, 0.12)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          width: 32,
          height: 32,
          borderRadius: 10,
          background: iconBg,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon size={16} color={iconColor} strokeWidth={2.2} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          data-testid={`${testIdPrefix}-action-needed-title`}
          style={{ margin: 0, fontFamily: FONT, fontSize: 13.5, fontWeight: 700, color: titleColor }}
        >
          {title}
        </p>
        <p
          style={{
            margin: '2px 0 0',
            fontFamily: FONT,
            fontSize: 11.5,
            color: subtitleColor,
            lineHeight: 1.35,
            fontWeight: 500,
            opacity: isSetup ? 0.9 : 0.85,
          }}
        >
          {subtitle}
        </p>
      </div>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '6px 10px',
          borderRadius: 9999,
          background: ctaBg,
          color: '#FFFFFF',
          fontFamily: FONT,
          fontSize: 11,
          fontWeight: 700,
          boxShadow: ctaShadow,
          flexShrink: 0,
        }}
      >
        {ctaLabel}
        <ArrowRight size={11} strokeWidth={2.4} />
      </span>
    </button>
  );
}

export default VenueActionNeededBanner;
